import { ImsObject } from 'app/shared/collections/ims_object';
import { DataWithLocationArray } from 'app/shared/collections/time/ims_time_collection.factory';
import moment from 'moment-timezone';
import { IMSVisualiser } from 'app/views/visualiser';
import { IMSAggregator } from 'app/views/aggregators/aggregator';
import { IWindowService } from 'angular';
import { ImsImageryLayersService } from '../ImsImageryLayersService';
import { AbstractGeoDataMap } from './AbstractGeoDataMap';
import { CanvasChoroplethMap } from './types/CanvasChoroplethMap';
import { createPrimitivePointMap } from './types/PrimitivePointMap';
import { createPrimitiveChoroplethMap } from './types/PrimitiveChoroplethMap';
import { ResolutionConfig } from './GeoDataService';
import { round } from 'lodash';

/**
 * Geo data map factory.
 *
 * @param {number} id should be unique
 * @param {DataWithLocationArray} data
 * @param {IMSVisualiser} imsVisualiser
 * @param {IMSAggregator} imsAggregator: If null, will use no aggregation
 * @param {angular.IWindowService} $window
 * @param {ImsImageryLayersService} imsImageryLayersService
 * @param resolutionConfig
 * @param {boolean} usePrimitives - We only use the Canvas implementation. The primitive implementation is being
 * kept at the moment for easy switching in case we find an issue with the canvas method.
 * @returns {AbstractGeoDataMap}
 */
export function createGeoDataMap(
    id: number,
    data: DataWithLocationArray,
    imsVisualiser: IMSVisualiser,
    imsAggregator: IMSAggregator,
    $window: IWindowService,
    imsImageryLayersService: ImsImageryLayersService,
    resolutionConfig: ResolutionConfig,
    usePrimitives: boolean = false,
): AbstractGeoDataMap {
    if (!usePrimitives) {
        return new CanvasChoroplethMap(
            id,
            data,
            imsVisualiser,
            imsAggregator,
            $window,
            imsImageryLayersService,
            resolutionConfig.choroplethSizeMetres,
            resolutionConfig.choroplethRenderSizeInPixels,
            resolutionConfig.resolutionMultiplier,
        );
    } else {
        // use primitives
        if (!imsAggregator) {
            return createPrimitivePointMap(id, data, imsVisualiser);
        } else {
            return createPrimitiveChoroplethMap(id, data, imsVisualiser, imsAggregator);
        }
    }
}

function calculateTimeDifferenceFromUnix(startPoint: ImsObject, endPoint: ImsObject) {
    return (
        Math.round(
            Math.abs(
                moment(endPoint.resource.timestamp).unix() -
                    moment(startPoint.resource.timestamp).unix(),
            ) / 60,
        ) / 60
    );
}

function heatMapIterationCalculator(
    startPoint: ImsObject,
    endPoint: ImsObject,
    maxHeatMapPoints: number,
    amountOfHoursRequested: number,
    amountOfPointsReturned: number,
    heatMapChunkSize: number,
): number {
    if (amountOfPointsReturned < heatMapChunkSize) {
        return 1;
    } else {
        //first off, calculate how many hours we get from the first and the last point
        let amountOfHoursReturned = calculateTimeDifferenceFromUnix(startPoint, endPoint);
        let estimatedNumberOfIterationsAmountFromHours: number =
            amountOfHoursRequested / amountOfHoursReturned;
        //take a stab at how many points we're going to need
        let estimatedPointsFromHours =
            estimatedNumberOfIterationsAmountFromHours * heatMapChunkSize;
        let progressPoints;
        if (estimatedPointsFromHours < maxHeatMapPoints) {
            //use the estimatedPoints for the progress bar
            progressPoints = estimatedPointsFromHours;
        } else {
            //use the max points for the progress bar
            progressPoints = maxHeatMapPoints;
        }
        return round(progressPoints / heatMapChunkSize, 2);
    }
}

export async function estimateIterations(
    statCollection: any,
    data: DataWithLocationArray,
    hoursRequested: number,
    maxPoints: number,
    heatMapChunkSize,
) {
    try {
        const startPoint: ImsObject = await statCollection.getAsync(data[0].id);
        const endPoint: ImsObject = await statCollection.getAsync(data.slice(-1)[0].id);
        return heatMapIterationCalculator(
            startPoint,
            endPoint,
            maxPoints,
            hoursRequested,
            data.length,
            heatMapChunkSize,
        );
    } catch (e) {
        return 0;
    }
}
